import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { HeartPulse, Sparkles, ExternalLink, X, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Spark = ({ trend, outcomes, onPick }) => {
  if (!trend.length) return null;
  const W = 560, H = 120, P = 8;
  const days = trend.map(t => t.day);
  const xs = trend.length === 1 ? [W / 2] : trend.map((_, i) => P + (i * (W - 2 * P)) / (trend.length - 1));
  const y = (s) => H - P - ((s / 100) * (H - 2 * P));
  const pts = trend.map((t, i) => `${xs[i]},${y(t.score)}`).join(' ');
  const last = trend[trend.length - 1];
  const dayX = (day) => {
    if (trend.length === 1) return day === days[0] ? xs[0] : null;
    if (day < days[0] || day > days[days.length - 1]) return null;
    const t0 = new Date(days[0]).getTime(), t1 = new Date(days[days.length - 1]).getTime();
    const td = new Date(day).getTime();
    return t1 === t0 ? W / 2 : P + ((td - t0) / (t1 - t0)) * (W - 2 * P);
  };
  const marks = (outcomes || []).map(o => ({ ...o, x: dayX(o.day) })).filter(o => o.x !== null);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28" data-testid="rfp-health-sparkline">
      {[80, 60].map(g => (
        <line key={g} x1={P} x2={W - P} y1={y(g)} y2={y(g)} stroke={g === 80 ? '#a7f3d0' : '#fde68a'} strokeDasharray="4 4" strokeWidth="1" />
      ))}
      {trend.length > 1 && <polyline points={pts} fill="none" stroke="#4ABFCF" strokeWidth="2.5" strokeLinejoin="round" />}
      {marks.map((o, i) => (
        <g key={i} data-testid={`rfp-health-outcome-${o.outcome}-${i}`} onClick={() => onPick(o)}
          style={{ cursor: 'pointer' }}>
          <line x1={o.x} x2={o.x} y1={P + 12} y2={H - P} stroke={o.outcome === 'won' ? '#10b981' : '#f43f5e'}
            strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
          <circle cx={o.x} cy={P + 6} r="9" fill="transparent" />
          <polygon points={o.outcome === 'won'
            ? `${o.x - 4},${P + 10} ${o.x + 4},${P + 10} ${o.x},${P + 2}`
            : `${o.x - 4},${P + 2} ${o.x + 4},${P + 2} ${o.x},${P + 10}`}
            fill={o.outcome === 'won' ? '#10b981' : '#f43f5e'}>
            <title>{`${o.outcome === 'won' ? 'Won' : 'Lost'} — ${o.name} (${o.day}) — click for details`}</title>
          </polygon>
        </g>
      ))}
      {trend.map((t, i) => (
        <circle key={t.day} cx={xs[i]} cy={y(t.score)} r={i === trend.length - 1 ? 4 : 2.5} fill="#0F2040">
          <title>{`${t.day} — score ${t.score} (${t.stale} stale · ${t.dup_pairs} dup pairs · ${t.pending_recs} pending recs)`}</title>
        </circle>
      ))}
      <text x={xs[xs.length - 1] - 6} y={y(last.score) - 8} fontSize="11" fontWeight="700" fill="#0F2040" textAnchor="end">{last.score}</text>
    </svg>
  );
};

export const HealthDialog = ({ open, onOpenChange, health, onOpenCleanup, onFilterStale }) => {
  const [trend, setTrend] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [picked, setPicked] = useState(null);
  const [recapDay, setRecapDay] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setPicked(null);
    axios.get(`${API_URL}/api/rfp/boilerplate/health/trend`, { withCredentials: true })
      .then(res => { setTrend(res.data?.trend || []); setOutcomes(res.data?.outcomes || []); })
      .catch(() => {});
    axios.get(`${API_URL}/api/rfp/boilerplate/health/schedule`, { withCredentials: true })
      .then(res => setRecapDay(res.data?.weekday ?? 0))
      .catch(() => {});
  }, [open]);

  const saveRecapDay = (v) => {
    setRecapDay(v);
    axios.put(`${API_URL}/api/rfp/boilerplate/health/schedule`, { weekday: v }, { withCredentials: true })
      .catch(() => {});
  };

  if (!health) return null;
  const rows = [
    ['Stale entries (>12 months untouched)', health.stale, health.penalties?.stale],
    ['Possible duplicate pairs', health.dup_pairs, health.penalties?.duplicates],
    ['Pending cleanup recommendations', health.pending_recs, health.penalties?.pending],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="rfp-health-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040] flex items-center gap-2">
            <HeartPulse className="w-4 h-4 text-[#4ABFCF]" />Library Health — {health.score} · {health.label}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-slate-500">
          Freshness score across {health.total} active capabilities. It improves as Clean &amp; Consolidate
          recommendations are actioned — the trend below records one point per day the library is reviewed.
        </p>
        {trend.length > 0 ? (
          <>
            <Spark trend={trend} outcomes={outcomes} onPick={setPicked} />
            {picked && (
              <div className={`rounded-md border px-3 py-2 space-y-1 ${picked.outcome === 'won'
                ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                data-testid="rfp-health-outcome-detail">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[9px] uppercase font-bold ${picked.outcome === 'won'
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-rose-100 text-rose-700 border-rose-300'}`}>
                    {picked.outcome}
                  </Badge>
                  <span className="text-[11px] font-semibold text-[#0F2040] truncate">{picked.name}</span>
                  {picked.client_name && <span className="text-[10px] text-slate-400">· {picked.client_name}</span>}
                  <span className="text-[10px] text-slate-400">· {picked.day}</span>
                  <span className="flex-1" />
                  <button onClick={() => { onOpenChange(false); navigate(`/rfp-response?project=${picked.id}`); }}
                    className="text-[10px] text-[#4ABFCF] font-semibold flex items-center gap-0.5 hover:underline"
                    data-testid="rfp-health-outcome-open">
                    <ExternalLink className="w-2.5 h-2.5" />Open RFP
                  </button>
                  <button onClick={() => setPicked(null)} className="text-slate-300 hover:text-slate-500"
                    data-testid="rfp-health-outcome-close"><X className="w-3 h-3" /></button>
                </div>
                <p className="text-[10px] text-slate-600">
                  {picked.note ? (picked.outcome === 'lost' ? `Loss note: ${picked.note}` : picked.note)
                    : `No ${picked.outcome === 'lost' ? 'loss note' : 'win summary'} recorded for this RFP.`}
                </p>
                {(picked.reasons || []).length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap" data-testid="rfp-health-outcome-reasons">
                    {picked.reasons.map(t => (
                      <Badge key={t} variant="outline" className="text-[8px] bg-rose-100 text-rose-700 border-rose-300">{t}</Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
            {outcomes.length > 0 && (
              <p className="text-[10px] text-slate-500 flex items-center gap-3" data-testid="rfp-health-winrate">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500 inline-block" />
                  {outcomes.filter(o => o.outcome === 'won').length} won</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-500 inline-block" />
                  {outcomes.filter(o => o.outcome === 'lost').length} lost</span>
                <span className="font-semibold text-[#0F2040]">
                  Win rate {Math.round((outcomes.filter(o => o.outcome === 'won').length / outcomes.length) * 100)}%
                </span>
                {(() => {
                  const cnt = {};
                  outcomes.forEach(o => (o.reasons || []).forEach(t => { cnt[t] = (cnt[t] || 0) + 1; }));
                  const top = Object.entries(cnt).sort((a, b) => b[1] - a[1]).slice(0, 3);
                  return top.length ? (
                    <span className="text-rose-600 font-medium" data-testid="rfp-health-loss-patterns">
                      Top loss reasons: {top.map(([t, n]) => `${t} ×${n}`).join(' · ')}
                    </span>
                  ) : null;
                })()}
                <span className="text-slate-400">click a marker for the win/loss note</span>
              </p>
            )}
          </>
        ) : (
          <p className="text-[10px] text-slate-400">No trend history yet — the score is snapshotted daily as the library is used.</p>
        )}
        <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
          {rows.map(([label, count, pen], ri) => (
            <div key={label} className="flex items-center gap-2 px-3 py-1.5 text-[11px]">
              <span className="text-slate-600 flex-1">{label}</span>
              {ri === 0 && count > 0 && onFilterStale && (
                <button onClick={() => { onOpenChange(false); onFilterStale(); }}
                  className="text-[10px] text-[#4ABFCF] font-semibold hover:underline"
                  data-testid="rfp-health-filter-stale">View stale entries</button>
              )}
              <span className="font-semibold text-[#0F2040]">{count}</span>
              <span className={`text-[10px] w-12 text-right ${pen > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>−{pen} pts</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center gap-2">
          {recapDay !== null ? (
            <label className="flex items-center gap-1.5 text-[10px] text-slate-500"
              title="The weekly health recap (in-app notification + email to admins) is sent on this day">
              Weekly recap day
              <select value={recapDay} onChange={e => saveRecapDay(Number(e.target.value))}
                className="h-6 text-[10px] border border-slate-200 rounded-md px-1 bg-white text-slate-600"
                data-testid="rfp-health-recap-day">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d, i) => (
                  <option key={d} value={i}>{d}</option>
                ))}
              </select>
              <span className="text-slate-400">in-app + email</span>
              <button onClick={async () => {
                setPreviewBusy(true);
                try {
                  const res = await axios.post(`${API_URL}/api/rfp/boilerplate/health/recap-preview`, {}, { withCredentials: true });
                  toast.success(`Preview sent to ${res.data?.to}`);
                } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send the preview'); }
                finally { setPreviewBusy(false); }
              }} disabled={previewBusy}
                className="flex items-center gap-1 text-[#4ABFCF] font-semibold hover:underline disabled:opacity-50"
                title="Email yourself the weekly recap right now to see what it looks like"
                data-testid="rfp-health-recap-preview">
                {previewBusy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Send className="w-2.5 h-2.5" />}
                Email me a preview
              </button>
            </label>
          ) : <span />}
          <Button size="sm" className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white"
            onClick={() => { onOpenChange(false); onOpenCleanup(); }} data-testid="rfp-health-open-cleanup">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />Open Clean &amp; Consolidate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
